// Game-mark identity — the key rules shared by every browser-local list that
// remembers a DECISION about a specific game (#120 saved, #654 minimized).
//
// Extracted from savedGames when the minimized list arrived. A mark list is
// worthless if its key is wrong in either direction: too coarse and starring
// game 1 of a doubleheader lights up game 2; too fine and the same game seen on
// Kalshi and on Polymarket becomes two entries. Both stores need the SAME
// answer, so the answer lives here once rather than being re-derived per list.
//
// ── The two keys, and when each applies ─────────────────────────────────
//
// `gameMarkId` = toHydraGameKeyLive(baseGameKey(rowKey)) — the primary id:
//
//   - `baseGameKey` strips the synthetic |spread|/|total|/|multi| suffixes so a
//     panel row and its underlying game resolve to the same mark.
//   - `toHydraGameKeyLive` alpha-sorts the team codes, so the SAME game on two
//     venues collapses to ONE entry, while KEEPING the doubleheader gameNo, so
//     an MLB doubleheader's game 1 and game 2 stay TWO entries.
//
// `collapsedGameKey` = toHydraGameKey(baseGameKey(rowKey)) — the coarse
// fallback, `date|TEAM_LO|TEAM_HI`. It DROPS the gameNo and so merges a
// doubleheader. Never use it as a primary id; it exists only to match rows
// that genuinely cannot express a game number (see isSportBearing).
//
// ── Why a date is never stored alongside a mark ─────────────────────────
//
// Every id embeds its game's `YYYY-MM-DD`, so `gameDateFromMarkId` reads it
// back out. A mark list therefore persists ids and nothing else — no second
// field to drift out of sync with the key it describes.

import {
  baseGameKey,
  parseGameKeyV2,
  toHydraGameKey,
  toHydraGameKeyLive,
} from '@/services/analyticsGameKey';

/** The subset of a GameRow a mark list needs to identify a game. Structural so
 *  a GameRow, an archived row, or a test fixture all satisfy it without
 *  importing the full view-layer type. */
export interface MarkableGame {
  key: string;
  sport: string;
  date: string;
  awayCode: string;
  homeCode: string;
}

/**
 * Canonical mark id for any dashboard row key, or null when the row isn't a
 * real game (series sentinel dates, event tickers, malformed keys). A null id
 * is the "not markable" signal every mark control gates on.
 */
export function gameMarkId(rowKey: string | null | undefined): string | null {
  return toHydraGameKeyLive(baseGameKey(rowKey));
}

/**
 * The collapsed `date|TEAM_LO|TEAM_HI` form of a row key or a mark id — the
 * most specific identity the analytics surface is capable of expressing.
 *
 * Used ONLY to match rows that cannot carry a game number. Never use it as a
 * primary id: it merges a doubleheader.
 */
export function collapsedGameKey(key: string | null | undefined): string | null {
  return toHydraGameKey(baseGameKey(key));
}

/** True when a row key carries a sport, i.e. it can express a specific game
 *  including a doubleheader's game number. Live rows do; rows projected from
 *  /api/analytics/games (`date|TEAM_A|TEAM_B`) do not. */
export function isSportBearing(rowKey: string | null | undefined): boolean {
  return parseGameKeyV2(baseGameKey(rowKey))?.sport != null;
}

/** The `YYYY-MM-DD` a mark id refers to, or null when the id is malformed.
 *  Lets a mark list age itself out without persisting a parallel date field. */
export function gameDateFromMarkId(id: string | null | undefined): string | null {
  return parseGameKeyV2(id)?.date ?? null;
}

/**
 * True when `viewerId` is the signed-in user that a loaded mark list belongs to.
 *
 * Every read and every write goes through this, so a mark store FAILS CLOSED: a
 * list belonging to someone else — or to nobody — is neither rendered nor
 * mutated. The reset at the auth boundary (authStore.purgeUserCaches) is the
 * primary defence; this is what makes a future path to unauthenticated that
 * forgets to reset a non-event rather than a cross-account leak.
 *
 * The viewer is passed in rather than read from authStore for two reasons: it
 * keeps the mark stores leaves with no store-to-store import (authStore imports
 * THEM for the reset), and it keeps the check reactive — a component subscribed
 * to the auth user re-renders when it changes, which a read inside this
 * function would not.
 */
export function ownedBy(state: { userId: string | null }, viewerId: string | null): boolean {
  return viewerId != null && state.userId === viewerId;
}
